import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { UserService } from './open-api/user.service';
import { sha256 } from 'js-sha256';
import { LocalStorageService } from './utils/local-storage.service';
import { ThemeService } from '@/core/services/theme.service';
import { TranslationManagerService } from '@/core/services/translation-manager.service';
import { TtlCacheStore } from '@/core/utils/cache/ttl-cache-store';
import { IntegrationService } from '@/core/services/open-api/integration.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
    public loggedIn = false;
    public loggedIn$ = new BehaviorSubject<boolean>(this.loggedIn);

    constructor(
        private router: Router,
        private userService: UserService,
        private jwtHelper: JwtHelperService,
        private themeModeService: ThemeService,
        private readonly translationManager: TranslationManagerService,
        private readonly integrationService: IntegrationService,
    ) {
        if (this.authenticated) {
            this.setLoggedIn(true);
        }
    }

    setLoggedIn(value: boolean): void {
        this.loggedIn$.next(value);
        this.loggedIn = value;
    }

    login(username: string, password: string, keepLogin: boolean): Observable<string> {
        return this.userService.login(username, sha256(password), keepLogin).pipe(
            map((token: string) => {
                LocalStorageService.storeToken(token);
                this.setLoggedIn(true);
                return token;
            }),
        );
    }

    ssoLogin(ssoToken: string): Observable<string> {
        return this.userService.ssoLogin(ssoToken).pipe(
            map((token: string) => {
                LocalStorageService.storeToken(token);
                this.setLoggedIn(true);
                return token;
            }),
        );
    }

    logout(): void {
        LocalStorageService.onLogout();
        this.themeModeService.resetThemeMode();
        this.translationManager.setTranslationsToBrowserLang();
        // Rate-limit timers are not managed by TtlCacheStore and must be cleared explicitly.
        this.integrationService.clearRateLimitState();
        // clear all caches (user, account, layer, tile, etc.)
        TtlCacheStore.resetAll();
        this.setLoggedIn(false);
        this.userService.logoutFinished();
        void this.router.navigate(['login']);
    }

    get authenticated(): boolean {
        // TODO the angular jwt operations are not able to detect invalid tokens as long as they can still be parsed with errors
        //      we should consider using jwt-decode library or other methods to detect invalid token structure
        const token = LocalStorageService.getToken();
        if (!token) {
            return false;
        }
        return !this.jwtHelper.isTokenExpired(token);
    }

    /**
     * Returns true if the user is authenticated and has a valid templateId.
     * The templateId is created during the onboarding process.
     * This method is used to determine if the user is allowed to access the application or has to complete the onboarding process.
     * @returns boolean - True if the user is authenticated and has a valid templateId, false otherwise.
     */
    public get authenticatedValidUser(): boolean {
        const token = LocalStorageService.getToken();
        const profile = LocalStorageService.getProfile();

        const tokenExpired = this.jwtHelper.isTokenExpired(token ?? '');
        const hasTemplateId = profile !== null && profile.templateId !== null;

        return !tokenExpired && hasTemplateId;
    }
}
